import { Link, createFileRoute } from "@tanstack/react-router";
import { Briefcase, Lightbulb, Sparkles, type LucideIcon } from "lucide-react";

import { CihClosingCta } from "~/components/cih/closing-cta";
import { CihLandingHero } from "~/components/cih/landing-hero";
import { LibraryLink } from "~/components/cih/library-link";
import { CihReveal } from "~/components/cih/reveal";
import { CihSectionHeader } from "~/components/cih/section-header";
import { Button } from "~/components/ui/button";
import { NamedIcon } from "~/components/ui/named-icon";
import {
  getPublishedGuidesFn,
  type GuideRow,
} from "~/lib/functions/cih/guides";
import {
  getPublishedPlaybooksFn,
  type PlaybookWithRelations,
} from "~/lib/functions/cih/playbooks";
import { BRAND_URL } from "~/lib/shared/brand";
import {
  buildPlaybooksIndexSections,
  type LibraryEntry,
  type LibrarySection,
  type LibrarySectionKey,
} from "~/lib/shared/cih/library-shelves";
import {
  createBrandMeta,
  createBreadcrumbStructuredData,
} from "~/lib/shared/meta";
import { READINESS_PUBLIC_PATH } from "~/lib/shared/readiness/routes";

const PARENT_URL = `${BRAND_URL}/coaching-business`;
const CANONICAL_URL = `${PARENT_URL}/playbooks`;

export const Route = createFileRoute("/_public/coaching-business/playbooks/")({
  staticData: { title: "Playbooks" },
  head: () => ({
    ...createBrandMeta({
      title: "Coaching Business Playbooks | Dream Coach Match",
      description:
        "The business and marketing frameworks the best coaches reference — analyzed through the lens of building a coaching practice.",
      ogUrl: CANONICAL_URL,
      canonical: CANONICAL_URL,
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          createBreadcrumbStructuredData([
            { name: "Home", url: BRAND_URL },
            { name: "Coaching Business", url: PARENT_URL },
            { name: "Playbooks", url: CANONICAL_URL },
          ]),
        ),
      },
    ],
  }),
  loader: async () => {
    // Guides are fetched too, not just playbooks — the shelf vocabulary is
    // shared, so a guide could in principle be shelved onto `canon` and would
    // need to render here rather than going missing.
    const [guides, playbooks] = await Promise.all([
      getPublishedGuidesFn(),
      getPublishedPlaybooksFn(),
    ]);
    return { guides, playbooks };
  },
  component: PlaybooksBrowsePage,
});

type GuideWithSlug = GuideRow & { slug: string };
type PlaybookWithSlug = PlaybookWithRelations & { slug: string };
type LibraryEntryUnion = LibraryEntry<GuideWithSlug, PlaybookWithSlug>;
type LibrarySectionUnion = LibrarySection<GuideWithSlug, PlaybookWithSlug>;

function sectionCopy(key: LibrarySectionKey): {
  label: string;
  title: string;
  description: string | null;
} {
  switch (key) {
    case "start_here":
      return {
        label: "Playbooks",
        title: "Start here",
        description: "Six playbooks to read first, in order.",
      };
    case "canon":
      return {
        label: "Playbooks",
        title: "Canon",
        description: "The full library, in curriculum order.",
      };
    default:
      return { label: "Playbooks", title: "More playbooks", description: null };
  }
}

function PlaybooksBrowsePage() {
  const { guides, playbooks } = Route.useLoaderData();

  const guidesWithSlug = guides.filter(
    (g): g is GuideWithSlug => g.slug != null,
  );
  const playbooksWithSlug = playbooks.filter(
    (p): p is PlaybookWithSlug => p.slug != null,
  );

  const sections = buildPlaybooksIndexSections(
    guidesWithSlug,
    playbooksWithSlug,
  );

  return (
    <>
      <CihLandingHero
        breadcrumb={[
          { label: "Home", href: "/" },
          { label: "Coaching Business", href: "/coaching-business" },
          { label: "Playbooks" },
        ]}
        accent="green"
        eyebrow="Coaching Intelligence"
        title="Coaching business playbooks"
        lede="The business and marketing frameworks the best coaches reference — analyzed through the lens of building a coaching practice."
        cta={
          <>
            <Link to={READINESS_PUBLIC_PATH}>
              <Button variant="primary" size="lg">
                Take the diagnostic →
              </Button>
            </Link>
            <Link to="/coaching-business">
              <Button variant="default" size="lg">
                Browse business guides
              </Button>
            </Link>
          </>
        }
      />

      {sections.length === 0 ? (
        <EmptyState />
      ) : (
        sections.map((section) => (
          <PlaybookShelfSection key={section.key} section={section} />
        ))
      )}

      <ExploreMore />

      <CihClosingCta
        kicker="Coach Business Builder"
        headline="Build the practice you actually want."
        cta={
          <Link to={READINESS_PUBLIC_PATH}>
            <Button variant="primary" size="lg">
              Take the diagnostic →
            </Button>
          </Link>
        }
      />
    </>
  );
}

function PlaybookShelfSection({
  section,
}: {
  readonly section: LibrarySectionUnion;
}) {
  const copy = sectionCopy(section.key);

  return (
    <>
      <CihSectionHeader label={copy.label} title={copy.title} />
      {copy.description ? (
        <div className="mx-auto -mt-4 max-w-7xl px-4 pb-6 xl:px-0">
          <p className="max-w-xl text-base leading-normal text-gray-500">
            {copy.description}
          </p>
        </div>
      ) : null}
      <div className="mx-auto max-w-7xl px-4 pb-14 md:pb-20 xl:px-0">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
          {section.items.map((entry, index) => (
            <CihReveal
              key={`${entry.type}-${entry.item.slug}`}
              delay={(index % 2) * 0.06}
            >
              <PlaybookLibraryCard entry={entry} />
            </CihReveal>
          ))}
        </div>
      </div>
    </>
  );
}

function PlaybookLibraryCard({ entry }: { readonly entry: LibraryEntryUnion }) {
  const attribution =
    entry.type === "playbook" ? (entry.item.thoughtLeader?.name ?? null) : null;

  return (
    <LibraryLink
      entry={entry}
      className="group flex h-full flex-col gap-3 rounded-2xl border border-gray-100 bg-white p-7 shadow-xs transition duration-300 hover:-translate-y-0.5 hover:border-gray-200 hover:shadow-md md:p-8"
    >
      {attribution ? (
        <div className="text-[10px] font-medium tracking-[0.22em] text-gray-400 uppercase">
          {attribution}
        </div>
      ) : null}
      <h3 className="font-heading text-2xl font-medium tracking-tight text-gray-900">
        {entry.item.title}
      </h3>
      {entry.item.description ? (
        <p className="text-sm leading-relaxed text-gray-500">
          {entry.item.description}
        </p>
      ) : null}
      <span className="mt-auto pt-3 text-sm font-medium text-gray-900">
        Read{" "}
        <span className="inline-block transition-transform duration-300 group-hover:translate-x-0.5">
          →
        </span>
      </span>
    </LibraryLink>
  );
}

type ExploreLink = {
  title: string;
  description: string;
  linkText: string;
  icon: LucideIcon;
  to: "/coaching-business" | "/coaching-intelligence" | "/discovery";
};

// Onward paths for visitors the playbook list itself didn't hook: the
// stage-matched guides, the wider hub, and the marketplace itself.
const EXPLORE_LINKS: ExploreLink[] = [
  {
    title: "Business guides",
    description:
      "Tactical, stage-matched guides for your practice — from first clients to systems that scale.",
    linkText: "Browse guides",
    icon: Briefcase,
    to: "/coaching-business",
  },
  {
    title: "Coaching Intelligence",
    description:
      "The whole knowledge layer: frameworks, training programs, organizations, and expert articles.",
    linkText: "Explore the hub",
    icon: Lightbulb,
    to: "/coaching-intelligence",
  },
  {
    title: "Work with a coach",
    description:
      "Sometimes the fastest way through a framework is a coach who's lived it. Find your match.",
    linkText: "Find your coach",
    icon: Sparkles,
    to: "/discovery",
  },
];

function ExploreMore() {
  return (
    <section className="mx-auto max-w-7xl px-4 pb-16 md:pb-20 xl:px-0">
      <CihReveal>
        <div className="text-xs font-medium tracking-[0.22em] text-gray-400 uppercase">
          Keep exploring
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
          {EXPLORE_LINKS.map((item) => (
            <Link
              key={item.title}
              to={item.to}
              className="group flex h-full flex-col gap-3 rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/60 via-white to-white p-7 transition duration-300 hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-md md:p-8"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-green-50 transition-colors group-hover:bg-green-100">
                <item.icon className="h-5 w-5 text-green-700" />
              </div>
              <h3 className="font-heading text-xl font-medium tracking-tight text-gray-900 md:text-2xl">
                {item.title}
              </h3>
              <p className="text-sm leading-relaxed text-gray-500">
                {item.description}
              </p>
              <span className="mt-auto pt-3 text-sm font-medium text-gray-900">
                {item.linkText}{" "}
                <span className="inline-block transition-transform duration-300 group-hover:translate-x-0.5">
                  →
                </span>
              </span>
            </Link>
          ))}
        </div>
      </CihReveal>
    </section>
  );
}

function EmptyState() {
  return (
    <section className="mx-auto max-w-md px-4 py-20 text-center">
      <NamedIcon name="target" className="mx-auto h-10 w-10 text-gray-300" />
      <p className="mt-4 text-sm text-gray-500">
        No playbooks published yet. Check back soon.
      </p>
    </section>
  );
}
